import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Users, Globe, Zap, ShieldCheck, FileText, TrendingUp } from 'lucide-react';

const stats = [
  { value: '10,000+', label: 'Invoices Created' },
  { value: '170+', label: 'Currencies Supported' },
  { value: '60s', label: 'Average Time to Invoice' },
  { value: '100%', label: 'Free to Start' },
];

const values = [
  {
    icon: <Zap className="h-6 w-6 text-indigo-500" />,
    title: 'Speed First',
    description:
      'We built Quick Free Invoice Tool so any freelancer or small business can go from blank page to sent invoice in under 60 seconds — no tutorials needed.',
  },
  {
    icon: <ShieldCheck className="h-6 w-6 text-emerald-500" />,
    title: 'Privacy by Design',
    description:
      'Your billing data is yours. We do not sell data, track you across the web, or require a credit card to start creating professional invoices.',
  },
  {
    icon: <Globe className="h-6 w-6 text-purple-500" />,
    title: 'Built for the World',
    description:
      'Support for 170+ global currencies, VAT, GST, SST, and multi-language invoice labels means our free invoice tool works wherever your clients are.',
  },
  {
    icon: <FileText className="h-6 w-6 text-blue-500" />,
    title: 'Professional Output',
    description:
      'Every invoice and quotation you create is formatted to a premium standard — downloadable as a clean PDF or editable Word (.docx) file instantly.',
  },
  {
    icon: <TrendingUp className="h-6 w-6 text-orange-500" />,
    title: 'Growth-Ready',
    description:
      'Start free and unlock Pro features — real-time client read receipts, analytics dashboards, and priority support — when your business needs more.',
  },
  {
    icon: <Users className="h-6 w-6 text-rose-500" />,
    title: 'Community Driven',
    description:
      'We listen to our users — freelancers, contractors, consultants, and agency owners — and ship features that solve real invoicing pain points fast.',
  },
];

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Quick Free Invoice Tool',
  description:
    'Learn about Quick Free Invoice Tool — the free online invoice generator built for freelancers, small businesses, and contractors worldwide.',
  url: 'https://quickfreeinvoicetool.com/about',
  publisher: {
    '@type': 'Organization',
    name: 'QuickFreeInvoice',
    url: 'https://quickfreeinvoicetool.com',
    logo: {
      '@type': 'ImageObject',
      url: 'https://quickfreeinvoicetool.com/logo.png',
    },
    sameAs: ['https://www.facebook.com/QuickFreeInvoiceTool'],
  },
};

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About Quick Free Invoice Tool — Free Online Invoice Generator for Freelancers & Small Businesses"
        description="Quick Free Invoice Tool is a 100% free online invoice generator built for freelancers, contractors, and small business owners. Create professional PDF & Word invoices in under 60 seconds. No sign-up required."
        keywords="about quick free invoice tool, free invoice generator company, online invoice maker, freelancer invoicing software, small business billing tool, invoice creator about us"
        canonical="https://quickfreeinvoicetool.com/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full">
            Our Story
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
            About Quick Free Invoice Tool — Built for Freelancers & Small Business Owners Worldwide
          </h1>
          <p className="text-lg text-indigo-100 max-w-2xl mx-auto leading-relaxed">
            We built Quick Free Invoice Tool because billing clients should not cost money. Our mission is to give every freelancer, contractor, and small business owner a fast, free, and professional invoicing solution — no subscriptions, no hidden fees, no sign-up walls.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 bg-slate-50 border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-center">
            {stats.map((stat) => (
              <div key={stat.label}>
                <div className="text-4xl font-extrabold text-indigo-600 font-heading">{stat.value}</div>
                <div className="text-sm text-slate-500 mt-1 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-slate-900">
            Our Mission: Make Professional Invoicing Free and Instant for Everyone
          </h2>
          <div className="prose prose-lg max-w-none text-slate-600 space-y-4">
            <p>
              Quick Free Invoice Tool was created with one clear goal: <strong>remove every barrier between completing work and getting paid</strong>. Traditional invoicing software charges monthly fees, requires lengthy setup, and often demands a credit card just to try it. We said no to all of that.
            </p>
            <p>
              Our free online invoice generator lets you create a professional invoice in under 60 seconds — no account needed, no credit card required. Fill in your business details, add your line items, choose from 170+ currencies, set your tax rate (VAT, GST, SST, or custom), and download a pixel-perfect PDF or an editable Word document.
            </p>
            <p>
              We serve <strong>freelancers</strong>, <strong>independent consultants</strong>, <strong>trade contractors</strong>, <strong>creative agencies</strong>, <strong>e-commerce sellers</strong>, and <strong>small business owners</strong> in over 50 countries. Our invoice generator handles the billing complexity so you can focus on the work you love.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-slate-900">
              What We Stand For — The Principles Behind Quick Free Invoice Tool
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Six core values guide every decision we make about the product and how we treat our users.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {values.map((v) => (
              <Card key={v.title} className="border-slate-200/80 hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mb-4">
                    {v.icon}
                  </div>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{v.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8 text-slate-900">
            What Quick Free Invoice Tool Offers — Key Features of Our Free Invoice Generator
          </h2>
          <div className="space-y-6">
            {[
              {
                title: 'Free PDF & Word Invoice Downloads — No Watermarks',
                body: 'Every invoice you create is available as a clean, professional PDF and an editable Microsoft Word (.docx) file at zero cost. No watermarks, no locked formats, no upgrade prompts to unlock your own file.',
              },
              {
                title: 'Free Quotation & Estimate Builder — Convert to Invoice in One Click',
                body: 'Create price quotations and project estimates for clients using the same simple builder. Once approved, convert any quotation into a final invoice instantly — a workflow used by thousands of freelancers and contractors.',
              },
              {
                title: 'Multi-Currency Invoicing — 170+ Global Currencies Supported',
                body: 'Bill clients in USD, EUR, GBP, AUD, CAD, INR, MYR, SGD, and 160+ other currencies. Our invoice generator auto-formats amounts, separators, and currency symbols correctly for every locale.',
              },
              {
                title: 'Automatic VAT, GST & SST Tax Calculations on Invoices',
                body: 'Add multiple tax tiers — VAT (EU/UK), GST (Australia, Canada, India, Singapore), SST (Malaysia), or custom sales tax (USA) — and the tool calculates totals automatically.',
              },
              {
                title: 'Real-Time Client Read Receipts & Invoice Tracking (Pro)',
                body: 'Upgrade to Pro and know the exact moment a client opens your invoice. Track outstanding balances, overdue invoices, and payment status from a central analytics dashboard.',
              },
              {
                title: 'Professional Invoice Templates — Modern, Clean, Print-Ready',
                body: 'Choose from multiple invoice layout templates designed for maximum legibility, brand consistency, and client trust. All templates are mobile-responsive and print-optimised.',
              },
            ].map((item) => (
              <div key={item.title} className="border-l-4 border-indigo-500 pl-6">
                <h3 className="font-heading font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 leading-relaxed text-sm">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8 text-slate-900">
            Who Uses Quick Free Invoice Tool? Real Use Cases Across Industries
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { role: 'Freelance Web Developers & Designers', desc: 'Bill clients for hourly work, milestones, and retainers with professional branded invoices.' },
              { role: 'Independent Consultants & Coaches', desc: 'Create custom invoices for consulting sessions, workshops, and advisory services.' },
              { role: 'Trade Contractors & Builders', desc: 'Itemize materials, labour hours, equipment, and subcontractor costs on formal tax invoices.' },
              { role: 'Creative Agencies', desc: 'Generate project invoices and send branded quotations to enterprise clients quickly.' },
              { role: 'E-Commerce Sellers', desc: 'Create product sales invoices with automatic GST/VAT calculations for cross-border orders.' },
              { role: 'Tutors & Training Providers', desc: 'Invoice students and corporate clients for educational services, courses, and workshops.' },
              { role: 'Healthcare & Wellness Practitioners', desc: 'Bill patients and insurance companies for clinical services and consultations.' },
              { role: 'Event Planners & Photographers', desc: 'Create event service invoices with deposits, milestone billing, and final balance invoices.' },
            ].map((item) => (
              <div key={item.role} className="bg-white border border-slate-200 rounded-lg p-4 flex gap-3">
                <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2 shrink-0" />
                <div>
                  <div className="font-semibold text-slate-900 text-sm">{item.role}</div>
                  <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Ready to Create Your First Free Professional Invoice?
          </h2>
          <p className="text-indigo-100 text-lg mb-8">
            Join thousands of freelancers and businesses already using Quick Free Invoice Tool. No sign-up required to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button size="lg" className="bg-white text-indigo-600 hover:bg-indigo-50 font-bold">
                Create a Free Invoice Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/guide">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Read the Invoice Guide
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
